import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { extractEmail, extractFile, money } from "@/lib/apiClient";
import { CameraCapture } from "@/components/CameraCapture";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  UploadCloud,
  Mail,
  FileText,
  Loader2,
  Sparkles,
  X,
  CheckCircle2,
  AlertTriangle,
  Copy,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ConfidenceBadge } from "@/components/ConfidenceBadge";
import { GmailConnectCard } from "@/components/GmailConnectCard";
import { ForwardingAddressCard } from "@/components/ForwardingAddressCard";
import { TrustedSenders } from "@/components/TrustedSenders";

const SAMPLE_EMAIL = `From: billing@figma.com
Subject: Your Figma invoice INV-FIG-99213

Hi team,

Thank you for your business. Here is your monthly invoice.

Invoice Number: INV-FIG-99213
Invoice Date: 2026-05-08
Due Date: 2026-06-07
Bill To: D20 Ltd

Description                         Qty    Unit Price     Amount
Figma Organization plan (seats)      18      45.00        810.00
Figma Dev Mode add-on                18      12.00        216.00

Subtotal:   1026.00
Tax (8%):     82.08
Total Due:  1108.08 USD

Please remit payment within 30 days.`;

export default function Upload() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [emailText, setEmailText] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]);
  const inputRef = useRef();

  const runEmail = async () => {
    setBusy(true);
    const t = toast.loading("AI is reading the invoice…");
    try {
      const inv = await extractEmail(emailText, user);
      toast.dismiss(t);
      inv.flagged
        ? toast.warning(`Extracted — flagged for review (${Math.round(inv.min_confidence * 100)}%)`)
        : toast.success(`Extracted & coded ${inv.supplier}`);
      navigate(`/invoice/${inv.id}`);
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setBusy(false);
    }
  };

  const runBulk = useCallback(async (files) => {
    const list = Array.from(files);
    if (list.length === 1) {
      // single file → go straight to detail
      setBusy(true);
      const t = toast.loading("AI is reading the invoice…");
      try {
        const inv = await extractFile(list[0], user);
        toast.dismiss(t);
        navigate(`/invoice/${inv.id}`);
      } catch (e) {
        toast.dismiss(t);
        toast.error(e?.response?.data?.detail || "Extraction failed");
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    setQueue(list.map((f) => ({ name: f.name, status: "queued" })));
    let done = 0;
    for (let i = 0; i < list.length; i++) {
      setQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "reading" } : item)));
      try {
        const inv = await extractFile(list[i], user);
        done++;
        setQueue((q) =>
          q.map((item, idx) => (idx === i ? { ...item, status: "done", invoice: inv } : item))
        );
      } catch (e) {
        setQueue((q) =>
          q.map((item, idx) =>
            idx === i ? { ...item, status: "error", error: e?.response?.data?.detail || "Failed" } : item
          )
        );
      }
    }
    setBusy(false);
    toast.success(`Batch complete — ${done}/${list.length} invoices coded`);
  }, [user, navigate]);

  const handleFiles = useCallback(
    (files) => {
      if (!files?.length) return;
      runBulk(files);
    },
    [runBulk]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const STATUS_ICON = {
    queued: <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />,
    reading: <Loader2 className="h-4 w-4 animate-spin text-primary" />,
    done: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
    error: <AlertTriangle className="h-4 w-4 text-red-500" />,
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Upload & Ingest</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Drop one file or a whole batch — the AI extracts every field and suggests ledger codes.
        </p>
      </div>

      <Tabs defaultValue="file">
        <TabsList data-testid="ingest-tabs" className="grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="file" data-testid="tab-file">
            <UploadCloud className="h-4 w-4 mr-2" /> Upload Files
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-2" /> Paste Email
          </TabsTrigger>
        </TabsList>

        <TabsContent value="file" className="mt-5 space-y-4">
          <CameraCapture user={user} disabled={busy} />
          <div
            data-testid="upload-drag-drop-zone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => !busy && inputRef.current?.click()}
            className={cn(
              "relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-center cursor-pointer transition-colors",
              dragging ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"
            )}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.txt,.png,.jpg,.jpeg,.webp"
              className="hidden"
              data-testid="file-input"
              onChange={(e) => handleFiles(e.target.files)}
            />
            {busy && queue.length === 0 ? (
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
            ) : (
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <UploadCloud className="h-7 w-7" />
              </span>
            )}
            <p className="mt-4 font-medium">Drop invoices here, or click to browse (multiple allowed)</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, TXT, or scanned image (PNG/JPG/WEBP) · OCR reads photos & scans</p>
          </div>

          {queue.length > 0 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="batch-results">
              <div className="border-b border-border px-4 py-3 text-sm font-medium">
                Batch — {queue.filter((q) => q.status === "done").length}/{queue.length} coded
              </div>
              <div className="divide-y divide-border">
                {queue.map((item, i) => (
                  <div key={`${item.name}-${i}`} className="flex items-center gap-3 px-4 py-3" data-testid={`batch-row-${i}`}>
                    <span className="flex h-5 w-5 items-center justify-center">{STATUS_ICON[item.status]}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {item.invoice ? item.invoice.supplier || item.name : item.name}
                      </div>
                      {item.status === "error" && <div className="text-xs text-red-500">{item.error}</div>}
                      {item.invoice && (
                        <div className="text-xs text-muted-foreground font-mono">
                          {money(item.invoice.total, item.invoice.currency)} · {item.invoice.status}
                        </div>
                      )}
                    </div>
                    {item.invoice && (
                      <>
                        <ConfidenceBadge value={item.invoice.min_confidence} showLabel={false} />
                        <button
                          data-testid={`batch-open-${i}`}
                          onClick={() => navigate(`/invoice/${item.invoice.id}`)}
                          className="text-primary hover:text-primary/80"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {!busy && (
                <div className="border-t border-border px-4 py-3 flex justify-end">
                  <Button data-testid="view-dashboard-btn" variant="outline" size="sm" onClick={() => navigate("/")}>
                    View Dashboard
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="email" className="mt-5 space-y-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" /> Paste invoice email text
              </label>
              <div className="flex gap-2">
                <button
                  data-testid="load-sample-email-btn"
                  onClick={() => setEmailText(SAMPLE_EMAIL)}
                  className="text-xs text-primary hover:underline"
                >
                  Use sample
                </button>
                {emailText && (
                  <button
                    data-testid="clear-email-btn"
                    onClick={() => setEmailText("")}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
            </div>
            <Textarea
              data-testid="email-textarea"
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              placeholder="Paste the full invoice email including line items, totals and tax…"
              className="min-h-[280px] font-mono text-xs d20-scroll resize-none"
            />
          </div>
          <Button
            data-testid="extract-email-btn"
            disabled={busy || !emailText.trim()}
            onClick={runEmail}
            className="w-full sm:w-auto"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Extract & Code Invoice
          </Button>
        </TabsContent>
      </Tabs>

      <GmailConnectCard />
      <ForwardingAddressCard />
      <TrustedSenders />

      <div className="rounded-xl border border-border bg-card p-5 flex gap-4">
        <Copy className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Smart safeguards:</span> D20 auto-codes every line, flags
          anything under <span className="font-mono text-foreground">80%</span> for review, warns you about{" "}
          <span className="text-foreground">possible duplicates</span>, and can auto-approve small high-confidence
          invoices per your <span className="text-foreground">Approval Rules</span>.
        </div>
      </div>
    </div>
  );
}
